import { ActionIcon, Button, Checkbox, Group, Loader, Stack, Text, TextInput, Title, Tooltip } from "@mantine/core";
import { useDebouncedState, useDisclosure } from "@mantine/hooks";
import { IconPlus, IconX } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { useState } from "react";
import { GroupMembersRepository, UserMembershipSearchRepository } from "shared/components/organization/groups/data";
import { groupsQueryOptions } from "shared/graphql/groups";
import { useGraphqlMutation } from "shared/hooks/useGraphql";
import { usePermissionCheck } from "shared/stores/oidc";
import { logger } from "shared/utils/logger";
import { some } from "shared/utils/maybe";
import { notify } from "shared/utils/notify";
import { qk } from "shared/utils/qk";
import { graphql } from "src/gql";
import { MemberLikeFragment, Permission } from "src/gql/graphql";
import { container } from "tsyringe";

const AddMembers = ({ groupId, initialMembers, close }: { groupId: string; initialMembers: string[]; close: () => void }) => {
    const [newMembers, setNewMembers] = useState<string[]>([]);
    const [usersSearchQuery, setUsersSearchQuery] = useDebouncedState("", 300);

    const usersSearchLoader = container
        .resolve(UserMembershipSearchRepository)
        .get({ search: some(usersSearchQuery).takeIf((it) => it.length > 0) });
    const users = usersSearchLoader.useItems();
    const groupMembersLoader = container.resolve(GroupMembersRepository).get({ id: groupId });

    const addUsers = useGraphqlMutation(
        graphql(`
            mutation MemberDetailsAddMember($groupId: UUID!, $userIds: [UUID!]!) {
                addUsersToGroup(groupId: $groupId, userIds: $userIds) {
                    id
                }
            }
        `),
    );

    return (
        <Stack h="50%">
            <Group>
                <Title order={3}>Members</Title>
                <Group flex={1}>
                    <Button
                        disabled={newMembers.length == 0}
                        onClick={() => {
                            addUsers
                                .mutateAsync({ groupId, userIds: newMembers })
                                .then(() =>
                                    groupMembersLoader.itemStore$.getState().upsert(...users.filter((mem) => newMembers.includes(mem.id))),
                                )
                                .then(() => qk.invalidate("group", "id", groupId, "directMembersCount"))
                                .then(() => notify.show.success("Added member"))
                                .then(close)
                                .catch(notify.catch);
                        }}
                        flex={1}
                        leftSection={<IconPlus size={16} />}
                    >
                        Add
                    </Button>
                    <Button variant="default" leftSection={<IconX size={16} />} onClick={close} flex={1}>
                        Cancel
                    </Button>
                </Group>
            </Group>
            <TextInput label="Search Users" placeholder="Enter a name..." onChange={(e) => setUsersSearchQuery(e.currentTarget.value)} />
            <Stack mah="50dvh" style={{ overflowY: "auto" }}>
                {!!users ? (
                    users.map((u) => {
                        const checked = newMembers.includes(u.id) || u.groups.some((group) => group.id == groupId);
                        return (
                            <Checkbox
                                key={u.id}
                                label={!!u.name?.trim() ? u.name.trim() : "No Name"}
                                description={!!u.loginEmail ? u.loginEmail : ""}
                                size="md"
                                checked={checked}
                                disabled={initialMembers.includes(u.id)}
                                onChange={() => setNewMembers(checked ? newMembers.filter((id) => id != u.id) : newMembers.concat(u.id))}
                            />
                        );
                    })
                ) : (
                    <Loader />
                )}
                <usersSearchLoader.LoadTrigger />
            </Stack>
        </Stack>
    );
};

const Member = ({
    member,
    canChangeMembership,
    onRemove,
}: {
    member: MemberLikeFragment;
    canChangeMembership: boolean;
    onRemove: () => void;
}) => {
    return (
        <Group key={member.id}>
            <Stack gap={0}>
                <Text>{!!member.name?.trim() ? member.name.trim() : "No name"}</Text>
                <Text fz="sm">{member.loginEmail}</Text>
            </Stack>
            <ActionIcon
                variant="transparent"
                color="text"
                size="sm"
                disabled={!canChangeMembership}
                onClick={onRemove}
                aria-label="Remove member"
            >
                <IconX size={16} />
            </ActionIcon>
        </Group>
    );
};

export const MemberDetails = ({ groupId }: { groupId: string }) => {
    const [opened, { open, close }] = useDisclosure();
    const canChangeMembership = usePermissionCheck(Permission.GroupUsersManage, groupId);
    const removeUser = useGraphqlMutation(
        graphql(`
            mutation MemberDetailsRemoveMember($userId: UUID!, $groupId: UUID!) {
                removeUserFromGroup(groupId: $groupId, userId: $userId) {
                    id
                }
            }
        `),
    );

    const loader = container.resolve(GroupMembersRepository).get({ id: groupId });
    const members = loader.useItems();

    const directMembersCount = useSuspenseQuery(groupsQueryOptions.directMembersCount(groupId));

    const MembersList = () => (
        <Stack h="50%">
            <Group>
                <Title order={3}>Members ({directMembersCount.data})</Title>

                <Tooltip label="You do not have permission to add members to this group" disabled={canChangeMembership}>
                    <Button variant="default" leftSection={<IconPlus size={16} />} onClick={open} disabled={!canChangeMembership}>
                        Add
                    </Button>
                </Tooltip>
            </Group>
            {members.length > 0 ? (
                <Stack style={{ overflowY: "auto" }}>
                    {members.map((member) => (
                        <Member
                            key={member.id}
                            member={member}
                            canChangeMembership={canChangeMembership}
                            onRemove={() => {
                                removeUser
                                    .mutateAsync({ userId: member.id, groupId: groupId })
                                    .then(() => loader.itemStore$.getState().remove(member.id))
                                    .then(() => directMembersCount.refetch())
                                    .then(() => notify.show.success("Removed user from group"))
                                    .catch(logger.error);
                            }}
                        />
                    ))}
                    <loader.LoadTrigger />
                </Stack>
            ) : (
                <Stack align="center">
                    <Text c="dimmed">No members found</Text>
                </Stack>
            )}
        </Stack>
    );

    return opened ? <AddMembers groupId={groupId} initialMembers={members.map((m) => m.id)} close={close} /> : <MembersList />;
};
